import * as _ from "underscore";

/* helpers */

const clone = (o: object) => JSON.parse(JSON.stringify(o));

const replaceAll = (str: string, find: string, replace: string) => {
  const escaped = find.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
  return str.replace(new RegExp(escaped, "ig"), replace);
};

// easily grep-able, and not part of any existing templating system, so will
// likely never match anything in output that we didn't generate ourselves
const withDelims = (s: string) => `~|${s}|~`;

/* strings */

const sensitiveLoanAppFieldToMask = {
  borrower1_first_name: "NAME",
  borrower1_middle_name: "NAME",
  borrower1_last_name: "NAME",
  borrower1_ssn: "SSN",
  borrower1_email: "EMAIL",
  borrower1_phone: "PHONE",
  borrower1_password: "PASSWORD",
  borrower1_current_address_line1: "ADDRESS",
  borrower2_first_name: "NAME",
  borrower2_middle_name: "NAME",
  borrower2_last_name: "NAME",
  borrower2_ssn: "SSN",
  borrower2_email: "EMAIL",
  borrower2_phone: "PHONE",
  borrower2_password: "PASSWORD",
  borrower2_current_address_line1: "ADDRESS",
};

const getSensitiveLoanAppStringToMaskMap = loanApp => {
  const map = {};
  _.mapObject(sensitiveLoanAppFieldToMask, (mask, field) => {
    const str = loanApp[field];
    if (typeof str === "string" && str !== "") {
      map[str] = mask;
    }
  });
  return map;
};

/* keys */

const sensitiveKeys = [
  {
    match: /birth/i,
    mask: "DOB",
  },
  {
    match: /dob/i,
    mask: "DOB",
  },
  {
    match: /password/i,
    mask: "PASSWORD",
  },
  {
    match: /s3_key/i,
    mask: "URL",
  },
  {
    match: /social.*security/i,
    mask: "SSN",
  },
  {
    match: /ssn/i,
    mask: "SSN",
  },
  {
    match: /token/i,
    mask: "TOKEN",
  },
  {
    match: /_url/i,
    mask: "URL",
  },
];

const getMaskForSensitiveKey = key => {
  for (let i = 0, l = sensitiveKeys.length; i < l; i += 1) {
    if (key.match(sensitiveKeys[i].match)) {
      return withDelims(sensitiveKeys[i].mask);
    }
  }
  return null;
};

const maskValueIfSensitive = (strToMaskMap, value) => {
  let masked = value;
  const keys = Object.keys(strToMaskMap);

  for (let i = 0, l = keys.length; i < l; i += 1) {
    const find = keys[i];
    const mask = strToMaskMap[find];

    // fewer chars will match too aggressively and mangle output,
    // and strings that small are not very identifiable anyway
    if (find.length > 3) {
      masked = replaceAll(masked, find, withDelims(mask));
    }
  }

  return masked;
};

/* SSNs */

const maskSSNs = (o: object) => {
  const str = JSON.stringify(o);
  const masked = str.replace(/\d{3}-\d{2}-\d{4}/gi, withDelims("SSN"));
  return JSON.parse(masked);
};

/* main */

const maskObject = (obj, sensitiveStrToMaskMap) => {
  const masked = clone(obj);
  const keys = Object.keys(obj);

  for (let i = 0, l = keys.length; i < l; i += 1) {
    const k = keys[i];
    const v = obj[k];

    const mask = getMaskForSensitiveKey(k);
    if (mask) {
      masked[k] = mask;
    } else if (typeof v === "object" && v != null && !_.isEmpty(v)) {
      masked[k] = maskObject(v, sensitiveStrToMaskMap);
    } else if (typeof v === "string" && v !== "") {
      masked[k] = maskValueIfSensitive(sensitiveStrToMaskMap, v);
    }
  }

  return masked;
};

const maskSensitiveInfo = (loanApp, sentryData) => {
  const map = getSensitiveLoanAppStringToMaskMap(loanApp);
  return maskSSNs(maskObject(sentryData, map));
};

// register with sentry's raven.js lib using Raven.setDataCallback()
// (see: https://github.com/getsentry/raven-js/blob/master/src/raven.js)
export const maskSensitiveInfoWithLoanAppContext = (loanApp = {}) => sentryData =>
  maskSensitiveInfo(loanApp, sentryData);
